import { isCaseOfflineEnabled } from '../offline_gating';
import { getCaseExperienceAdminPreviewMode } from './case_experience_api';

const SECTION_DB = 'aquifer-case-sections';
const SECTION_STORE = 'sections';
const CASE_STATE_ATTRIBUTE = 'data-case-experience--case-viewer-document-user-state-value';
const FEEDBACK_SUBMIT_BUTTON_UID = 'feedback_card_1_button';

// ponytail: memory is this page only. IndexedDB is what survives reload; a failed write is lost on refresh.
const memorySections = new Map();
let sectionDbPromise = null;
// Bumped when a continue is applied, so a restore that started earlier does not overwrite it.
let continueGeneration = 0;
// Answer saves and Continue share this chain so one cache write cannot drop the other.
let caseStateChain = Promise.resolve();

export function runOnCaseStateChain(execute) {
  const chained = caseStateChain.then(execute, execute);
  caseStateChain = chained.then(
    () => {},
    () => {},
  );
  return chained;
}

// Fetch every root section while online and keep the HTML for later.
export async function prefetchCaseSections(documentId, userId) {
  if (!isCaseOfflineEnabled() || browserSeemsOffline() || !documentId || !userId) return;
  await Promise.all(rootSectionUids().map((uid) => sectionHtml(documentId, userId, uid)));
}

// Online: post the unlock, then remember progress and refresh that section's cache.
// Offline or a failed post: return the cached pane in the same shape the server uses.
export async function routeCaseUnlock({ documentId, userId, currentSectionUid, continueButtonUid, post }) {
  if (shouldUseCachedSection()) {
    return (await cachedUnlockResponse({ documentId, userId, currentSectionUid, continueButtonUid })) || offlineFailure();
  }

  const response = await post();
  if (response?.networkError) {
    if (!isCaseOfflineEnabled()) return response;
    return (await cachedUnlockResponse({ documentId, userId, currentSectionUid, continueButtonUid })) || response;
  }
  if (response?.ok && isCaseOfflineEnabled()) return noteOnlineUnlock(documentId, userId, response);
  return response;
}

// After an offline reload, show the section the student had open.
export async function restoreOfflineCaseSection({ documentId, userId } = {}) {
  if (!shouldUseCachedSection() || !documentId || !userId) return false;

  const generation = continueGeneration;
  const cached = await readProgress(documentId, userId);
  if (!cached || generation !== continueGeneration) return false;

  const uid = cached.offlineActiveSectionUid;
  let restored = false;
  if (uid && uid !== visibleRootUid()) {
    const html = await readSection(userId, documentId, uid);
    if (html && generation === continueGeneration && renderPane(html)) {
      await markNavCurrent(documentId, userId, uid);
      restored = true;
    }
  }

  if (generation !== continueGeneration) return false;
  if (cached.state && typeof cached.state === 'object') {
    const state = mergeCaseState(readCaseState(), cached.state);
    writeCaseState(state);
    paintProgress(state);
    showSavedAnswers(state);
  }
  if (!restored) return false;

  document.dispatchEvent(new CustomEvent('case-experience:refresh-continue-buttons', {
    bubbles: true,
    detail: { documentId },
  }));
  return true;
}

// Offline case progress is on, and the browser looks offline.
function shouldUseCachedSection() {
  return isCaseOfflineEnabled() && browserSeemsOffline();
}

// Same shape continueToNextSection treats as a failed network call.
function offlineFailure() {
  return { ok: false, networkError: new Error('offline') };
}

// Offline when the browser says so, or when the app stored aquifer-online as 0.
export function browserSeemsOffline() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  try {
    return sessionStorage.getItem('aquifer-online') === '0';
  } catch (_error) {
    return false;
  }
}

// URL for one root section's server-rendered pane.
function sectionUrl(documentId, uid) {
  return `/documents/${documentId}/case_experience/section?${new URLSearchParams({ uid })}`;
}

// Use the network while online and store the result. Otherwise read the cache.
async function sectionHtml(documentId, userId, uid) {
  if (!documentId || !userId || !uid) return null;
  const url = sectionUrl(documentId, uid);

  if (!browserSeemsOffline()) {
    try {
      const html = await fetchSection(url);
      if (html) {
        if (isCaseOfflineEnabled()) await saveSection(userId, documentId, uid, html);
        return html;
      }
    } catch (error) {
      if (!isCaseOfflineEnabled()) throw error;
    }
  }

  return isCaseOfflineEnabled() ? readSection(userId, documentId, uid) : null;
}

// GET the pane. Null unless the body is a turbo stream.
async function fetchSection(url) {
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'text/vnd.turbo-stream.html' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok || typeof response.text !== 'function') return null;
  const html = await response.text();
  return html?.includes('<turbo-stream') ? html : null;
}

// Answer Continue from the cached next pane and queue the real unlock for later.
async function cachedUnlockResponse({ documentId, userId, currentSectionUid, continueButtonUid }) {
  const nextUid = nextSectionUid(currentSectionUid);
  const html = nextUid && await readSection(userId, documentId, nextUid);
  if (!html) return null;

  try {
    const sync = await progressSync();
    await sync.queueCaseExperienceUnlock({
      documentId,
      userId,
      currentSectionUid,
      rootUids: rootSectionUids(),
      adminPreviewMode: getCaseExperienceAdminPreviewMode(),
    });
  } catch (error) {
    console.warn('[CaseOffline] failed to queue continue', error);
    return null;
  }

  continueGeneration += 1;
  const state = stateAfterContinue(currentSectionUid, nextUid, continueButtonUid);
  writeCaseState(state);
  paintProgress(state);
  await markNavCurrent(documentId, userId, nextUid);
  await rememberProgress(documentId, userId, { state, nextUid });
  return jsonResponse({
    state,
    next_revealed_section_uid: nextUid,
    turbo_streams: html,
  });
}

// After a successful unlock, store progress and refresh the cache for the section just opened.
async function noteOnlineUnlock(documentId, userId, response) {
  const data = await copyJson(response);
  if (!data) return response;

  await rememberProgress(documentId, userId, {
    state: data.state,
    nextUid: data.next_revealed_section_uid,
  });
  if (data.next_revealed_section_uid) void sectionHtml(documentId, userId, data.next_revealed_section_uid);

  // A real fetch body can only be read once. Test doubles often have json() and no clone().
  return typeof response.clone === 'function' ? response : jsonResponse(data);
}

// Enough of a fetch response for callers that only await json().
function jsonResponse(payload) {
  return { ok: true, json: async () => payload };
}

// Read the body without consuming it when the response can be cloned.
async function copyJson(response) {
  try {
    if (typeof response?.clone === 'function') return await response.clone().json();
    if (typeof response?.json === 'function') return await response.json();
  } catch (_error) {
    return null;
  }
  return null;
}

// Load the sync helper when it is needed, not when the case page starts.
async function progressSync() {
  const mod = await import('../../src/react/helpers/OfflineProgressSync');
  return mod.default;
}

// Cached progress for this student and case, or null if it cannot be read.
async function readProgress(documentId, userId) {
  try {
    const sync = await progressSync();
    return await sync.readCachedDocumentUserState(documentId, userId);
  } catch (_error) {
    return null;
  }
}

// Store the latest case state and which section is open.
// Caller runs this on the case-state chain. Do not call runOnCaseStateChain from here.
async function rememberProgress(documentId, userId, { state, nextUid } = {}) {
  if (!isCaseOfflineEnabled() || !documentId || !userId) return undefined;
  try {
    const sync = await progressSync();
    const existing = await sync.readCachedDocumentUserState(documentId, userId);
    const merged = mergeCaseState(existing?.state, state);
    await sync.rememberCaseExperienceActiveSection(documentId, userId, { state: merged, nextUid });
    return merged;
  } catch (error) {
    console.warn('[CaseOffline] failed to store case progress', error);
    return undefined;
  }
}

// Uid of the section currently shown in the active pane.
function visibleRootUid() {
  const pane = document.getElementById('case-experience-active-pane');
  const section = pane?.querySelector('[data-case-experience--case-viewer-target="section"]')
    || pane?.querySelector('.doc-section[data-level="1"]');
  return section?.dataset?.uid || null;
}

// Next root section recorded on the section that is open.
function nextSectionUid(currentSectionUid) {
  const pane = document.getElementById('case-experience-active-pane');
  if (!pane || !currentSectionUid) return null;
  // Compare the attribute directly. A uid can contain characters that break a selector.
  for (const node of pane.querySelectorAll('[data-uid]')) {
    if (node.getAttribute('data-uid') === currentSectionUid) {
      return node.getAttribute('data-case-next-root-section-uid');
    }
  }
  return null;
}

// Document user state currently stored on the case container.
function readCaseState() {
  const raw = document.getElementById('document-container')?.getAttribute(CASE_STATE_ATTRIBUTE);
  try {
    const parsed = JSON.parse(raw || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_error) {
    return {};
  }
}

// Write document user state back onto the case container.
function writeCaseState(state) {
  document.getElementById('document-container')?.setAttribute(CASE_STATE_ATTRIBUTE, JSON.stringify(state));
}

function fieldUpdatedAt(field) {
  if (!field || typeof field !== 'object') return null;
  return typeof field.updatedAt === 'number' ? field.updatedAt : null;
}

function mergeNodeFields(current, incoming) {
  const merged = {
    ...(current && typeof current === 'object' && !Array.isArray(current) ? current : {}),
  };
  Object.entries(incoming).forEach(([key, field]) => {
    const incomingAt = fieldUpdatedAt(field);
    const currentAt = fieldUpdatedAt(merged[key]);
    // A missing timestamp still replaces, so a fresh write is not dropped.
    if (incomingAt != null && currentAt != null && incomingAt < currentAt) return;
    merged[key] = field;
  });
  return merged;
}

// Per field, the later updatedAt wins. The second state wins a tie.
export function mergeCaseState(current, incoming) {
  const base = current && typeof current === 'object' && !Array.isArray(current) ? current : {};
  const extra = incoming && typeof incoming === 'object' && !Array.isArray(incoming) ? incoming : {};
  const merged = { ...base };
  Object.entries(extra).forEach(([uid, node]) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) {
      merged[uid] = node;
      return;
    }
    merged[uid] = mergeNodeFields(merged[uid], node);
  });
  return merged;
}

// Put queued multiple-choice clicks and text answers back on the open section.
function showSavedAnswers(state) {
  const stimulus = window.Stimulus;

  Object.entries(state).forEach(([uid, node]) => {
    if (!node || typeof node !== 'object') return;
    const saved = node.value?.value;
    if (saved === true || saved === false) checkSavedChoice(uid, saved);
    if (typeof saved === 'string') fillSavedText(uid, saved);
    if (node.graded?.value === true) lockSavedSubmit(stimulus, uid);
  });

  refreshAnswerSubmitButtons(stimulus);
}

function uidForSelector(uid) {
  return window.CSS?.escape ? CSS.escape(uid) : String(uid);
}

function checkSavedChoice(uid, checked) {
  const answer = document.querySelector(`[data-answer-uid="${uidForSelector(uid)}"]`);
  if (!answer) return;

  answer.classList.toggle('checked', checked);
  answer.setAttribute('aria-checked', String(checked));
  const input = answer.querySelector('input[type="checkbox"], input[type="radio"]');
  if (!input) return;
  input.checked = checked;
  input.setAttribute('aria-checked', String(checked));
}

function fillSavedText(uid, text) {
  const textarea = document.querySelector(
    `[data-case-experience--notes-node-uid-value="${uidForSelector(uid)}"] textarea`,
  );
  if (!textarea || document.activeElement === textarea) return;
  textarea.value = text;
}

function lockSavedSubmit(stimulus, uid) {
  const escaped = uidForSelector(uid);
  const multipleChoice = stimulus?.getControllerForElementAndIdentifier(
    document.querySelector(`[data-case-experience--multiple-choice-node-uid-value="${escaped}"]`),
    'case-experience--multiple-choice',
  );
  if (multipleChoice) {
    multipleChoice.gradedValue = true;
    multipleChoice.inactiveValue = true;
    multipleChoice.applyPostGradedDomState();
    multipleChoice.lockSubmitButtonForGraded();
  }

  const notes = stimulus?.getControllerForElementAndIdentifier(
    document.querySelector(`[data-case-experience--notes-node-uid-value="${escaped}"]`),
    'case-experience--notes',
  );
  if (!notes?.hasTextareaTarget) return;
  notes.gradedValue = true;
  notes.textareaTarget.disabled = true;
}

function refreshAnswerSubmitButtons(stimulus) {
  if (!stimulus) return;

  document.querySelectorAll('[data-controller~="case-experience--multiple-choice"]').forEach((el) => {
    stimulus.getControllerForElementAndIdentifier(el, 'case-experience--multiple-choice')
      ?.updateSubmitButtonState();
  });
  document.querySelectorAll('[data-controller~="case-experience--notes"]').forEach((el) => {
    const notes = stimulus.getControllerForElementAndIdentifier(el, 'case-experience--notes');
    notes?.updateCharCount();
    notes?.updateSubmitButtonState();
  });
}

// Local progress after Continue: the current card is collapsed, the next card is open,
// and a continue button that counts toward progress is spent.
function stateAfterContinue(currentUid, nextUid, continueButtonUid, cachedState) {
  const state = JSON.parse(JSON.stringify(mergeCaseState(cachedState, readCaseState())));
  const at = Date.now();
  const flag = (value) => ({ value, updatedAt: at });
  const keep = (uid) => (state[uid] && typeof state[uid] === 'object' ? state[uid] : {});

  state[currentUid] = { ...keep(currentUid), collapsed: flag(true), visited: flag(true) };
  state[nextUid] = {
    ...keep(nextUid),
    invisible: flag(false),
    collapsed: flag(false),
    visited: flag(true),
  };
  if (progressMarkerUids().includes(continueButtonUid)) {
    state[continueButtonUid] = { ...keep(continueButtonUid), invisible: flag(true) };
  }
  return state;
}

// Continue buttons the server counts toward the progress percent.
function progressMarkerUids() {
  const raw = document.getElementById('document-container')?.getAttribute('data-progress-marker-uids');
  return raw ? JSON.parse(raw) : [];
}

// Same percent the server shows: spent markers over all markers, or 100 once feedback is submitted.
function paintProgress(state) {
  const markers = progressMarkerUids();
  if (!markers.length) return;

  const spent = markers.filter((uid) => state[uid]?.invisible?.value === true || state[uid]?.clicked?.value === true);
  const feedbackSubmitted = state[FEEDBACK_SUBMIT_BUTTON_UID]?.clicked?.value === true;
  const percent = feedbackSubmitted ? 100 : Math.trunc((spent.length / markers.length) * 100);

  document.querySelectorAll('[data-controller~="case-experience--progress-announcer"]').forEach((bar) => {
    bar.setAttribute('data-case-experience--progress-announcer-percent-value', percent);
  });
}

// Replace the active pane with a cached turbo stream.
function renderPane(html) {
  if (!html || typeof window.Turbo?.renderStreamMessage !== 'function') return false;
  window.Turbo.renderStreamMessage(html);
  return true;
}

// Heading inside a cached pane, used to replace an "Unavailable" nav label.
function sectionTitle(html) {
  if (!html) return '';
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const markup = parsed.querySelector('template')?.innerHTML || html;
  const heading = new DOMParser().parseFromString(markup, 'text/html').querySelector('h1, h2, h3');
  return heading?.textContent?.replace(/\s+/g, ' ').trim() || '';
}

// ponytail: the cache stores the pane only, so offline updates the nav rows already in the DOM.
// Upgrade: cache a nav partial per active section.
function rootSectionUids() {
  return [...document.querySelectorAll('#case-experience-section-nav [data-root-section-uid]')]
    .map((row) => row.getAttribute('data-root-section-uid'))
    .filter(Boolean);
}

async function markNavCurrent(documentId, userId, nextUid) {
  const nav = document.getElementById('case-experience-section-nav');
  if (!nav || !nextUid) return;

  const rows = [...nav.querySelectorAll('[data-root-section-uid]')];
  const index = rows.findIndex((row) => row.getAttribute('data-root-section-uid') === nextUid);
  if (index < 0) return;

  for (let i = 0; i <= index; i += 1) {
    const stateNode = rows[i].querySelector('[data-state]');
    if (!stateNode) continue;

    const wasLocked = stateNode.getAttribute('data-state') === 'locked';
    const current = i === index;
    const status = current ? 'current' : 'visited';
    stateNode.setAttribute('data-state', status);
    if (current) stateNode.setAttribute('aria-current', 'step');
    else stateNode.removeAttribute('aria-current');
    if (wasLocked) await paintUnlockedLabel(stateNode, documentId, userId, rows[i].getAttribute('data-root-section-uid'));
    paintNavStatus(stateNode, status);
  }
}

// Replace an "Unavailable" nav label with the cached section's heading.
async function paintUnlockedLabel(stateNode, documentId, userId, uid) {
  const label = stateNode.querySelector('.content-panel-label');
  const title = sectionTitle(await readSection(userId, documentId, uid));
  if (!label || !title || !/unavailable/i.test(label.textContent)) return;
  const numberPrefix = label.textContent.match(/^\d+\.\s*/);
  label.textContent = `${numberPrefix ? numberPrefix[0] : ''}${title}`;
}

function navLabel(node) {
  return node.querySelector('.content-panel-label')?.textContent.trim();
}

// Copy the icon and the aria status wording from the server-rendered sample row.
function paintNavStatus(stateNode, status) {
  const sample = document.getElementById('case-experience-offline-nav-icons')
    ?.content.querySelector(`[data-state="${status}"]`);
  if (!sample) return;

  const icon = sample.querySelector('.content-panel-icon-wrapper');
  const wrapper = stateNode.querySelector('.content-panel-icon-wrapper');
  if (icon && wrapper) wrapper.replaceChildren(...icon.cloneNode(true).childNodes);

  const label = navLabel(stateNode);
  const statusSuffix = sample.getAttribute('aria-label').slice(navLabel(sample).length);
  if (label) stateNode.setAttribute('aria-label', `${label}${statusSuffix}`);
}

// Open the section cache, creating the store the first time.
function openSectionDb() {
  if (sectionDbPromise) return sectionDbPromise;
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('no indexedDB'));

  sectionDbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(SECTION_DB, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(SECTION_STORE)) {
        request.result.createObjectStore(SECTION_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }).catch((error) => {
    sectionDbPromise = null;
    throw error;
  });
  return sectionDbPromise;
}

// Run one IndexedDB read or write against the section store.
function withSectionStore(mode, operation) {
  return openSectionDb().then((db) => new Promise((resolve, reject) => {
    const request = operation(db.transaction(SECTION_STORE, mode).objectStore(SECTION_STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }));
}

function sectionCacheKey(userId, documentId, uid) {
  return `user-${userId}:${sectionUrl(documentId, uid)}`;
}

// Keep the pane HTML in memory and in IndexedDB.
async function saveSection(userId, documentId, uid, html) {
  const key = sectionCacheKey(userId, documentId, uid);
  memorySections.set(key, html);
  if (typeof indexedDB === 'undefined') return;
  try {
    await withSectionStore('readwrite', (store) => store.put(html, key));
  } catch (error) {
    console.warn('[CaseOffline] failed to cache section', error);
  }
}

// Pane HTML from memory, then from IndexedDB.
async function readSection(userId, documentId, uid) {
  const key = sectionCacheKey(userId, documentId, uid);
  if (memorySections.has(key)) return memorySections.get(key);
  if (typeof indexedDB === 'undefined') return null;
  try {
    const html = await withSectionStore('readonly', (store) => store.get(key));
    if (html) memorySections.set(key, html);
    return html || null;
  } catch (_error) {
    return null;
  }
}
